import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Guests as GuestsApi } from '../lib/api';
import { Button } from './ui/button';
import { Download, X, Maximize, Minimize, Loader2, FileText, Clock, ZoomIn, ZoomOut } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

export default function PrepReader() {
  const { guestId } = useParams();
  const [guest, setGuest] = useState(null);
  const [error, setError] = useState('');
  const [fs, setFs] = useState(false);
  const [hideBar, setHideBar] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [pdfError, setPdfError] = useState('');
  const [zoom, setZoom] = useState(() => parseFloat(localStorage.getItem('prep-zoom')) || 1);
  const [width, setWidth] = useState(Math.min(window.innerWidth - 32, 1000));
  useEffect(() => {
    const onResize = () => setWidth(Math.min(window.innerWidth - 32, 1000));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const changeZoom = (d) => setZoom(z => { const n = Math.min(2.5, Math.max(0.5, +(z + d).toFixed(2))); localStorage.setItem('prep-zoom', String(n)); return n; });

  useEffect(() => {
    GuestsApi.get(guestId).then(setGuest).catch(() => setError('Guest not found'));
  }, [guestId]);

  useEffect(() => {
    const onFs = () => setFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const toggleFs = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen?.();
  };

  const tp = guest?.talkingPoints;
  const when = guest?.recordingDate
    ? new Date(guest.recordingDate).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: guest.recordingDate.length > 10 ? 'numeric' : undefined, minute: guest.recordingDate.length > 10 ? '2-digit' : undefined })
    : null;

  if (error) return <div className="min-h-screen grid place-items-center text-stone-500">{error}</div>;
  if (!guest) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

  return (
    <div className="h-screen flex flex-col bg-stone-900 text-stone-100" data-testid="prep-reader">
      <div
        className={`flex items-center gap-3 px-4 border-b border-stone-800 bg-stone-950 transition-all duration-300 overflow-hidden ${hideBar ? 'h-2 hover:h-12' : 'h-12'}`}
        onMouseEnter={() => hideBar && setHideBar(false)}
      >
        <FileText className="w-4 h-4 text-red-400 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium truncate" data-testid="prep-guest-name">{guest.name}{guest.episodeTitle ? <span className="text-stone-400 font-normal"> — {guest.episodeTitle}</span> : null}</div>
        </div>
        {when && <div className="hidden sm:flex items-center gap-1 text-[11.5px] text-stone-400"><Clock className="w-3.5 h-3.5" /> {when}</div>}
        {tp?.url && (
          <Button asChild size="sm" variant="ghost" className="h-8 text-stone-300 hover:text-white hover:bg-stone-800" data-testid="prep-download-btn">
            <a href={`${tp.url}?download=1`}><Download className="w-3.5 h-3.5 mr-1.5" /> Download</a>
          </Button>
        )}
        {tp?.url && (
          <div className="flex items-center gap-0.5 text-stone-300">
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:text-white hover:bg-stone-800" onClick={() => changeZoom(-0.15)} data-testid="prep-zoom-out" title="Smaller text"><ZoomOut className="w-3.5 h-3.5" /></Button>
            <span className="text-[11px] w-10 text-center tabular-nums" data-testid="prep-zoom-level">{Math.round(zoom * 100)}%</span>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:text-white hover:bg-stone-800" onClick={() => changeZoom(0.15)} data-testid="prep-zoom-in" title="Larger text"><ZoomIn className="w-3.5 h-3.5" /></Button>
          </div>
        )}
        <Button size="sm" variant="ghost" className="h-8 text-stone-300 hover:text-white hover:bg-stone-800" onClick={toggleFs} data-testid="prep-fullscreen-btn" title={fs ? 'Exit full screen' : 'Full screen'}>
          {fs ? <Minimize className="w-3.5 h-3.5" /> : <Maximize className="w-3.5 h-3.5" />}
        </Button>
        <Button size="sm" variant="ghost" className="h-8 text-stone-300 hover:text-white hover:bg-stone-800" onClick={() => setHideBar(true)} title="Hide this bar (hover top edge to bring it back)">
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>

      {tp?.url ? (
        <div className="flex-1 overflow-auto bg-stone-800" data-testid="prep-pdf-scroll">
          <div className="mx-auto py-4 flex flex-col items-center gap-4" style={{ width: Math.round(width * zoom) }}>
            <Document
              file={tp.url}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              onLoadError={(e) => setPdfError(e?.message || 'Could not load PDF')}
              loading={<div className="py-20 flex items-center gap-2 text-stone-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading talking points…</div>}
              error={<div className="py-20 text-stone-400 text-center">Couldn’t render this PDF in the browser. <a className="underline" href={`${tp.url}?download=1`}>Download it</a> instead.{pdfError ? <div className="text-[11px] mt-1 text-stone-500">{pdfError}</div> : null}</div>}
            >
              {Array.from({ length: numPages }, (_, i) => (
                <div key={i} className="shadow-2xl mb-4 bg-white" data-testid={`prep-page-${i + 1}`}>
                  <Page pageNumber={i + 1} width={Math.min(Math.round(width * zoom), window.innerWidth - 16)} renderAnnotationLayer={false} />
                </div>
              ))}
            </Document>
            {numPages > 0 && <div className="text-[11px] text-stone-500 pb-6">{numPages} page{numPages === 1 ? '' : 's'} · end of talking points</div>}
          </div>
        </div>
      ) : (
        <div className="flex-1 grid place-items-center text-center px-6">
          <div>
            <FileText className="w-10 h-10 mx-auto text-stone-600 mb-3" />
            <div className="text-[15px] font-medium">No talking points uploaded for {guest.name}</div>
            <div className="text-[13px] text-stone-400 mt-1">Open the guest record → <b>Prep</b> tab and drop a PDF there.</div>
          </div>
        </div>
      )}
    </div>
  );
}
